"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const util_1 = require("../util");
const { GET, PATCH, PUT, DELETE } = util_1.httpMethods;
/**
 * @classdesc Represents the class interfacing devices related API-I endpoints
 * @class
 */
class Devices {
    constructor(api) {
        this.api = api;
    }
    /**
     * Get devices list according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#devices-devices-get}
     * @param {Object} args={} - An object containing the arguments accepted by the API
     * @param {?string} args.withInactive - Optional. Set 'only' to return all internal maintenance owner providers
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an object of data or an error with the problem
     */
    async getDevices(args = {}) {
        const { withInactive } = args;
        const methodName = 'getDevices';
        // validate
        const validations = [(0, util_1.validateParam)(methodName, 'withInactive', withInactive, util_1.ValidationTypes.STRING)];
        (0, util_1.validateParams)(validations);
        const route = 'devices';
        const input = {
            query: {
                withInactive,
            },
        };
        const formattedInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(GET, route, formattedInput);
    }
    /**
     * Set planned device installed
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#node-devices-set-planned-device-installed-patch}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.nodeIdentifier - Required. The node identifier from the node
     * @param {!number} args.customerId - Required. The id of customer
     * @param {!string} args.port - Required. Device port
     * @param {!number} args.channel - Required. Device channel
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return a response object or an error with the problem.
     */
    async setDevicePlannedToInstalled(args) {
        const { nodeIdentifier, customerId, port, channel } = args;
        const methodName = 'setDevicePlannedToInstalled';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'port', port, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'port', port, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'channel', channel, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'channel', channel, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/common/${nodeIdentifier}/device/${port}/${channel}/install`;
        const input = {
            header: {
                customerId,
            },
            body: null,
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PATCH, route, endPointInput);
    }
    /**
     * Set channel data for device
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#node-devices-modify-device-channel-patch}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.nodeIdentifier - Required. The node identifier from the node
     * @param {!number} args.customerId - Required. The id of customer
     * @param {!string} args.port - Required. Device port
     * @param {!number} args.currentChannel - Required. Existing device channel
     * @param {!number} args.newChannel - Required. New device channel
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an response object or an error with the problem.
     */
    async setDeviceChannel(args) {
        const { nodeIdentifier, customerId, port, currentChannel, newChannel } = args;
        const methodName = 'setDeviceChannel';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'port', port, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'port', port, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'currentChannel', currentChannel, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'currentChannel', currentChannel, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'newChannel', newChannel, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'newChannel', newChannel, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/common/${nodeIdentifier}/device/${port}/${currentChannel}/channel/${newChannel}`;
        const input = {
            header: {
                customerId,
            },
            body: null,
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PATCH, route, endPointInput);
    }
    /**
     * Set height data for device
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#node-devices-modify-device-height-patch}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.nodeIdentifier - Required. The node identifier from the node
     * @param {!number} args.customerId - Required. The id of customer
     * @param {!string} args.port - Required. Device port
     * @param {!number} args.channel - Required. Device channel
     * @param {!string} args.newHeight - Required. New device height
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an response object or an error with the problem.
     */
    async setDeviceHeight(args) {
        const { nodeIdentifier, customerId, port, channel, newHeight } = args;
        const methodName = 'setDeviceHeight';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'port', port, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'port', port, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'channel', channel, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'channel', channel, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'newHeight', newHeight, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'newHeight', newHeight, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/common/${nodeIdentifier}/device/${port}/${channel}/height/${newHeight}`;
        const input = {
            header: {
                customerId,
            },
            body: null,
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PATCH, route, endPointInput);
    }
    /**
     * Attach a Device to a Node
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#node-devices-attach-device-to-node}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.nodeIdentifier - Required. The node identifier from the node
     * @param {!number} args.customerId - Required. The id of customer
     * @param {!string} args.connector - Required. Device connector (sdi, rj11, stereo)
     * @param {!number} args.channel - Required. Device channel
     * @param {!string} args.source - Required. Device source
     * @param {!string} args.height - Required. Device height
     * @param {!string} args.installationStatus - Required. Device installation status (PLANNED, INSTALLED)
     * @param {string} args.managementType - Optional. Device management type (commercial, test, research, etc)
     * @param {number} args.maintenanceOwnerId - Optional. Device maintenance owner id
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an response object or an error with the problem.
     */
    async attachDevice(args) {
        const { nodeIdentifier, connector, channel, customerId, ...device } = args;
        const methodName = 'attachDevice';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'connector', connector, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'connector', connector, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'channel', channel, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'channel', channel, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/common/${nodeIdentifier}/device/${connector}/${channel}`;
        const input = {
            header: {
                customerId,
            },
            body: device,
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PUT, route, endPointInput);
    }
    /**
     * Remove a Device from a Node
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#node-devices-detach-device-from-a-node}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.nodeIdentifier - Required. The node identifier from the node
     * @param {!number} args.customerId - Required. The id of customer
     * @param {!string} args.connector - Required. Device connector (sdi, rj11, stereo)
     * @param {!number} args.channel - Required. Device channel (0, 1, 2, 3)
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an response object or an error with the problem.
     */
    async detachDevice(args) {
        const { nodeIdentifier, connector, channel, customerId } = args;
        const methodName = 'detachDevice';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'connector', connector, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'connector', connector, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'channel', channel, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'channel', channel, util_1.ValidationTypes.NUMBER),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/common/${nodeIdentifier}/device/${connector}/${channel}`;
        const input = {
            header: {
                customerId,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(DELETE, route, endPointInput);
    }
}
exports.default = Devices;
//# sourceMappingURL=devices.js.map